import { hasActiveThemeFilters, normalizeThemeFilters } from './theme-filters.js';

const esc = value => String(value ?? '')
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;')
  .replaceAll("'", '&#039;');

const formatNumber = value => Number(value || 0).toLocaleString('ru-RU');

function skeletons() {
  return `<div class="theme-hub-skeleton theme-hub-skeleton-featured"></div>
    <div class="theme-hub-grid" aria-hidden="true">
      <div class="theme-hub-skeleton"></div><div class="theme-hub-skeleton"></div>
    </div>`;
}

function statePanel(kind, title, text, action = '') {
  return `<section class="theme-hub-state theme-hub-state-${kind}" role="${kind === 'error' ? 'alert' : 'status'}">
    <span class="theme-hub-state-mark" aria-hidden="true">${kind === 'error' ? '!' : '0'}</span>
    <h3>${esc(title)}</h3><p>${esc(text)}</p>${action}
  </section>`;
}

function themeCard(theme, model, featured = false) {
  const id = String(theme.id);
  const owned = model.ownedIds.has(id) || (model.userId && String(theme.author_id || '') === model.userId);
  const active = id === model.activeId;
  const action = owned
    ? (active
      ? '<button class="theme-hub-action is-active" type="button" disabled>Установлена</button>'
      : `<button class="theme-hub-action is-apply" type="button" data-theme-action="apply" data-theme-id="${esc(id)}">Применить</button>`)
    : `<button class="theme-hub-action is-buy" type="button" data-theme-action="buy" data-theme-id="${esc(id)}">Купить · ${formatNumber(theme.price)} ◉</button>`;

  return `<article class="theme-hub-card${featured ? ' is-featured' : ''}${active ? ' is-current' : ''}">
    <button class="theme-hub-preview" type="button" data-theme-action="preview" data-theme-id="${esc(id)}" aria-label="Посмотреть тему ${esc(theme.title || 'Без названия')}"><span class="theme-hub-media">${model.renderMedia(theme, { eager: featured })}</span><span class="theme-hub-view">Просмотр <span aria-hidden="true">↗</span></span></button>
    <div class="theme-hub-shade"></div>
    <div class="theme-hub-card-copy">
      <div class="theme-hub-card-top">
        <div><h3>${esc(theme.title || 'Без названия')}</h3><p>${esc(theme.author_name || 'Автор')}</p></div>
        ${active ? '<span class="theme-hub-current">Активна</span>' : ''}
      </div>
      <div class="theme-hub-card-foot">
        <span class="theme-hub-sales">${formatNumber(theme.sales)} покупок</span>
        ${action}
      </div>
    </div>
  </article>`;
}

function filterBar(model, filtersOpen = false) {
  const f = normalizeThemeFilters(model.filters);
  const typeButtons = [['all', 'Все'], ['photo', 'Фото'], ['gif', 'GIF'], ['video', 'Видео']]
    .map(([value, label]) => `<button type="button" class="theme-hub-segment${f.type === value ? ' is-selected' : ''}" data-filter-type="${value}" aria-pressed="${f.type === value}">${label}</button>`).join('');
  return `<section class="theme-hub-controls" aria-label="Фильтры тем">
    <div class="theme-hub-search-row"><label class="theme-hub-search"><input type="search" value="${esc(f.query)}" maxlength="80" autocomplete="off" placeholder="Название или автор" aria-label="Поиск темы" data-theme-search></label><button type="button" class="theme-hub-filter-toggle" data-theme-filter-toggle aria-expanded="${filtersOpen}" aria-controls="theme-hub-filter-options" aria-label="Фильтры тем"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M4 17h16"/><circle cx="9" cy="7" r="3"/><circle cx="15" cy="17" r="3"/></svg></button></div>
    <div class="theme-hub-control-row" id="theme-hub-filter-options" ${filtersOpen?'':'hidden'}>
      <div class="theme-hub-segments" role="group" aria-label="Тип темы">${typeButtons}</div>
      <label class="theme-hub-sort"><span>Сортировка</span><select data-theme-sort>
        <option value="popular"${f.sort === 'popular' ? ' selected' : ''}>Популярные</option>
        <option value="new"${f.sort === 'new' ? ' selected' : ''}>Новые</option>
        <option value="price_asc"${f.sort === 'price_asc' ? ' selected' : ''}>Сначала дешевле</option>
        <option value="price_desc"${f.sort === 'price_desc' ? ' selected' : ''}>Сначала дороже</option>
      </select></label>
    </div>
    <div class="theme-hub-scope" role="group" aria-label="Доступность">
      ${[['all', 'Все темы'], ['owned', 'Мои темы'], ['buy', 'Не куплены']].map(([value, label]) => `<button type="button" class="theme-hub-scope-btn${f.ownership === value ? ' is-selected' : ''}" data-filter-ownership="${value}" aria-pressed="${f.ownership === value}">${label}</button>`).join('')}
    </div>
  </section>`;
}

export function renderThemeHub(root, options) {
  if (!root) return;
  const activeEl = typeof document !== 'undefined' ? document.activeElement : null;
  const focusedSearch = Boolean(activeEl?.matches?.('[data-theme-search]'));
  const caret = focusedSearch ? activeEl.selectionStart : null;
  const filtersOpen = root.querySelector('[data-theme-filter-toggle]')?.getAttribute('aria-expanded') === 'true';
  const model = {
    ...options,
    filters: normalizeThemeFilters(options.filters),
    ownedIds: new Set((options.ownedIds || []).map(String)),
    userId: String(options.userId || ''),
    activeId: String(options.activeId || ''),
  };

  let content = '';
  if (model.loading) content = skeletons();
  else if (model.error) content = statePanel('error', 'Темы не загрузились', model.error, '<button type="button" class="theme-hub-retry" data-theme-retry>Попробовать снова</button>');
  else if (!model.totalCount) content = statePanel('empty', 'Каталог готовится', 'Одобренные темы появятся здесь после модерации.');
  else if (!model.themes.length) content = statePanel('empty', 'Ничего не найдено', 'Измените запрос или сбросьте фильтры.', '<button type="button" class="theme-hub-retry" data-theme-clear>Сбросить фильтры</button>');
  else {
    const featured = !hasActiveThemeFilters(model.filters) && model.themes.length > 2;
    const first = featured ? themeCard(model.themes[0], model, true) : '';
    const rest = (featured ? model.themes.slice(1) : model.themes).map(theme => themeCard(theme, model)).join('');
    content = `${first}<div class="theme-hub-grid">${rest}</div>`;
  }

  root.innerHTML = `${model.error && !model.totalCount ? '' : filterBar(model, filtersOpen)}
    ${model.notice ? `<div class="theme-hub-notice" role="status">${esc(model.notice)}</div>` : ''}
    <div class="theme-hub-results" aria-live="polite" aria-busy="${Boolean(model.loading)}">${content}</div>
    ${model.activeId && !model.error ? '<button class="theme-hub-reset-theme" type="button" data-theme-reset>Вернуть стандартное оформление</button>' : ''}`;

  if (focusedSearch) {
    const search = root.querySelector('[data-theme-search]');
    search?.focus();
    if (caret != null) search?.setSelectionRange(caret, caret);
  }

  root.querySelector('[data-theme-search]')?.addEventListener('input', event => model.onFilters({ ...model.filters, query: event.target.value }));
  root.querySelector('[data-theme-filter-toggle]')?.addEventListener('click', event => {
    const button = event.currentTarget;
    const open = button.getAttribute('aria-expanded') !== 'true';
    button.setAttribute('aria-expanded', String(open));
    root.querySelector('#theme-hub-filter-options').hidden = !open;
  });
  root.querySelector('[data-theme-sort]')?.addEventListener('change', event => model.onFilters({ ...model.filters, sort: event.target.value }));
  root.querySelectorAll('[data-filter-type]').forEach(button => button.addEventListener('click', () => model.onFilters({ ...model.filters, type: button.dataset.filterType })));
  root.querySelectorAll('[data-filter-ownership]').forEach(button => button.addEventListener('click', () => model.onFilters({ ...model.filters, ownership: button.dataset.filterOwnership })));
  root.querySelectorAll('[data-theme-action]').forEach(button => button.addEventListener('click', () => {
    const action = button.dataset.themeAction;
    if (action === 'preview') model.onPreview?.(button.dataset.themeId);
    if (action === 'buy') model.onBuy(button.dataset.themeId);
    if (action === 'apply') model.onApply(button.dataset.themeId);
  }));
  root.querySelector('[data-theme-clear]')?.addEventListener('click', model.onClearFilters);
  root.querySelector('[data-theme-retry]')?.addEventListener('click', model.onRetry);
  root.querySelector('[data-theme-reset]')?.addEventListener('click', model.onResetTheme);
}
